import type { CardProps } from '@mui/material';
import { Card, Divider, Skeleton, Stack, Typography } from '@mui/material';
import type { FC, ReactNode, JSX } from 'react';
import React, { memo } from 'react';
import { Iconify, OneLineTypography } from '@pflegenavi/web-components';
import {
  useFormatDate,
  useFormatTime,
  useFormatting,
} from '@pflegenavi/frontend/localization';
import {
  TIME_FORMAT,
  YEAR_MONTH_DAY_SHORT_FORMAT,
} from '@pflegenavi/shared/constants';
import { useTranslation } from 'react-i18next';
import { DropDownMenu } from './DropDownMenu';
import { CustomAlert } from './CustomAlert';

interface CashLinkViewProps extends ContentProps {
  notes?: string[];
  handleRemoveLink: () => void;
  groupId?: string;
  sx?: CardProps['sx'];
  disabled?: boolean;
  disabledTooltipTitle?: string;
  singleReceipt?: boolean;
  submitted?: boolean;
}

export const CashLinkView: FC<CashLinkViewProps> = memo(
  ({
    notes,
    groupId,
    handleRemoveLink,
    sx,
    disabled,
    disabledTooltipTitle,
    singleReceipt,
    submitted,
    ...props
  }) => {
    notes = notes?.filter((note) => note !== '');
    notes = notes?.length ? notes : undefined;

    return (
      <Card sx={{ p: 2, ...sx }}>
        <Stack
          sx={{
            gap: 2,
          }}
        >
          <Header
            linkedCount={props.linkedCount}
            handleRemoveLink={handleRemoveLink}
            groupId={groupId}
            disabled={disabled}
            disabledTooltipTitle={disabledTooltipTitle}
            singleReceipt={singleReceipt}
            submitted={submitted}
          />
          <Divider
            sx={{
              // Counter the card padding
              ml: -2,
              mr: -2,
            }}
          />
          <Content {...props} />
          {notes && <Notes notes={notes} />}
        </Stack>
      </Card>
    );
  }
);

interface NotesProps {
  notes: string[];
}

const Notes: FC<NotesProps> = ({ notes }) => {
  const { t } = useTranslation();
  return (
    <Stack
      sx={{
        gap: 1,
      }}
    >
      <Divider
        sx={{
          // Counter the card padding
          ml: -2,
          mr: -2,
        }}
      />
      <Typography
        variant="overline"
        sx={{
          color: 'grey.600',
        }}
      >
        {t('cashManagement.link.notes-title')}
      </Typography>
      {notes.map((note, index) => (
        <Stack
          key={index}
          sx={{
            borderRadius: 2,
            backgroundColor: 'grey.200',
            p: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: 'grey.900',
              maxWidth: 270,
            }}
          >
            {note}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
};

interface HeaderProps {
  handleRemoveLink: () => void;
  linkedCount: number;
  groupId?: string;
  disabled?: boolean;
  disabledTooltipTitle?: string;
  singleReceipt?: boolean;
  submitted?: boolean;
}

const Header: FC<HeaderProps> = ({
  handleRemoveLink,
  linkedCount,
  groupId,
  disabled,
  disabledTooltipTitle,
  singleReceipt,
  submitted,
}) => {
  const { t } = useTranslation();
  return (
    <Stack
      direction="row"
      sx={{
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <Stack
        direction="row"
        sx={{
          gap: 1.5,
          alignItems: 'center',
        }}
      >
        <Stack
          sx={{
            justifyContent: 'center',
            alignItems: 'center',
            height: 40,
            width: 40,
            backgroundColor: 'grey.300',
            borderRadius: 2,
          }}
        >
          <Iconify width={24} height={24} icon={'mdi:receipt-text'} />
        </Stack>
        <Stack
          sx={{
            gap: 0.5,
          }}
        >
          <OneLineTypography sx={{ fontWeight: 700 }} variant="subtitle2">
            {t('cashManagement.link.cash-link.title')}
          </OneLineTypography>
          <OneLineTypography sx={{ fontSize: 14 }} color="grey.600">
            {singleReceipt
              ? t('cashManagement.link.title-single-link')
              : t('cashManagement.link.title-multi-link')}
          </OneLineTypography>
        </Stack>
      </Stack>
      <DropDownMenu
        handleRemoveLink={handleRemoveLink}
        groupId={groupId}
        disabled={disabled}
        disabledTooltipTitle={disabledTooltipTitle}
        submitted={submitted}
      />
    </Stack>
  );
};

interface ContentProps {
  date: Date;
  cashAmountCents: number;
  cashSeparateFeeAmountCents: number;
  cashImmediateFeeAmountCents: number;
  linkedAmountCents: number;
  linkedSeparateFeeAmountCents: number;
  linkedImmediateFeeAmountCents: number;
  linkedCount: number;
  receiptAmountCents: number;
  receiptAdditionalFeeAmountCents: number;
  receiptIncludedFeeAmountCents: number;
  isLoading?: boolean;
  alwaysShowLinkedAmount?: boolean;
  hideReceiptAmount?: boolean;
}

const Content: FC<ContentProps> = ({
  date,
  cashAmountCents,
  cashSeparateFeeAmountCents,
  cashImmediateFeeAmountCents,
  receiptAmountCents,
  receiptAdditionalFeeAmountCents,
  receiptIncludedFeeAmountCents,
  linkedAmountCents,
  linkedSeparateFeeAmountCents,
  linkedImmediateFeeAmountCents,
  linkedCount,
  isLoading,
  hideReceiptAmount,
  alwaysShowLinkedAmount,
}) => {
  const { t } = useTranslation();
  const { fCurrencyCents } = useFormatting();
  const fDate = useFormatDate();
  const fTime = useFormatTime();

  const mismatch = cashAmountCents !== linkedAmountCents;
  const separateFeeMismatch =
    cashSeparateFeeAmountCents !== linkedSeparateFeeAmountCents;
  const immediateFeeMismatch =
    cashImmediateFeeAmountCents !== linkedImmediateFeeAmountCents;
  const multiLink = linkedCount > 1 || alwaysShowLinkedAmount;

  return (
    <Stack
      sx={{
        gap: 2,
      }}
    >
      <Stack
        sx={{
          gap: 1.5,
        }}
      >
        <Row
          isLoading={isLoading}
          icon={<DateIcon color="text.primary" />}
          label={t('cashManagement.link.date')}
          value={
            <Stack direction="row">
              <Typography
                sx={{
                  fontSize: 14,
                  color: 'text.primary',
                }}
              >
                {fDate(date, YEAR_MONTH_DAY_SHORT_FORMAT)},&nbsp;
              </Typography>
              <Typography
                sx={{
                  fontSize: 14,
                  color: 'grey.600',
                }}
              >
                {fTime(date, TIME_FORMAT)}
              </Typography>
            </Stack>
          }
        />
        <Row
          isLoading={isLoading}
          icon={<CashIcon color="text.primary" />}
          label={t('cashManagement.link.cash-amount')}
          value={
            <Typography
              sx={{
                fontSize: 14,
                color: 'text.primary',
              }}
            >
              {fCurrencyCents(cashAmountCents)}
            </Typography>
          }
        />
        {(cashImmediateFeeAmountCents || immediateFeeMismatch) && (
          <Row
            color="grey.600"
            isLoading={isLoading}
            icon={<DotIcon color="grey.600" />}
            label={t('cashManagement.link.cash-immediate-fee-amount')}
            value={
              <Typography
                sx={{
                  fontSize: 14,
                  color: 'grey.600',
                }}
              >
                {fCurrencyCents(cashImmediateFeeAmountCents)}
              </Typography>
            }
          />
        )}
        {(cashSeparateFeeAmountCents || separateFeeMismatch) && (
          <Row
            color="grey.600"
            isLoading={isLoading}
            icon={<DotIcon color="grey.600" />}
            label={t('cashManagement.link.cash-separate-fee-amount')}
            value={
              <Typography
                sx={{
                  fontSize: 14,
                  color: 'grey.600',
                }}
              >
                {fCurrencyCents(cashSeparateFeeAmountCents)}
              </Typography>
            }
          />
        )}
        {multiLink && (
          <Row
            isLoading={isLoading}
            icon={<LinkedIcon color="text.primary" />}
            label={`${t('cashManagement.link.linked-amount')} (${linkedCount})`}
            value={
              <Typography
                sx={{
                  fontSize: 14,
                  color: 'text.primary',
                }}
              >
                {fCurrencyCents(linkedAmountCents)}
              </Typography>
            }
          />
        )}
        {multiLink &&
          (linkedImmediateFeeAmountCents || immediateFeeMismatch) && (
            <Row
              color="grey.600"
              isLoading={isLoading}
              icon={<DotIcon color="grey.600" />}
              label={`${t(
                'cashManagement.link.linked-immediate-fee-amount'
              )} (${linkedCount})`}
              value={
                <Typography
                  sx={{
                    fontSize: 14,
                    color: 'grey.600',
                  }}
                >
                  {fCurrencyCents(linkedImmediateFeeAmountCents)}
                </Typography>
              }
            />
          )}
        {multiLink && (linkedSeparateFeeAmountCents || separateFeeMismatch) && (
          <Row
            color="grey.600"
            isLoading={isLoading}
            icon={<DotIcon color="grey.600" />}
            label={`${t(
              'cashManagement.link.linked-separate-fee-amount'
            )} (${linkedCount})`}
            value={
              <Typography
                sx={{
                  fontSize: 14,
                  color: 'grey.600',
                }}
              >
                {fCurrencyCents(linkedSeparateFeeAmountCents)}
              </Typography>
            }
          />
        )}
        {!hideReceiptAmount && (
          <Row
            isLoading={isLoading}
            icon={<ReceiptIcon color="text.primary" />}
            label={t('cashManagement.link.receipt-amount')}
            value={
              <Typography
                sx={{
                  fontSize: 14,
                  color: 'text.primary',
                }}
              >
                {fCurrencyCents(receiptAmountCents)}
              </Typography>
            }
          />
        )}
        {!hideReceiptAmount &&
          (receiptIncludedFeeAmountCents || immediateFeeMismatch) && (
            <Row
              color="grey.600"
              isLoading={isLoading}
              icon={<DotIcon color="grey.600" />}
              label={t('cashManagement.link.receipt-included-fee-amount')}
              value={
                <Typography
                  sx={{
                    fontSize: 14,
                    color: 'grey.600',
                  }}
                >
                  {fCurrencyCents(receiptIncludedFeeAmountCents)}
                </Typography>
              }
            />
          )}
        {!hideReceiptAmount &&
          (receiptAdditionalFeeAmountCents || separateFeeMismatch) && (
            <Row
              color="grey.600"
              isLoading={isLoading}
              icon={<DotIcon color="grey.600" />}
              label={t('cashManagement.link.receipt-additional-fee-amount')}
              value={
                <Typography
                  sx={{
                    fontSize: 14,
                    color: 'grey.600',
                  }}
                >
                  {fCurrencyCents(receiptAdditionalFeeAmountCents)}
                </Typography>
              }
            />
          )}
      </Stack>
      <CustomAlert variant={mismatch ? 'warning' : 'success'}>
        <Typography
          sx={{
            fontSize: 14,
          }}
        >
          {mismatch
            ? t('cashManagement.link.amount-mismatch')
            : t('cashManagement.link.amount-no-mismatch')}
        </Typography>
      </CustomAlert>
      {(cashSeparateFeeAmountCents || linkedSeparateFeeAmountCents) &&
      separateFeeMismatch ? (
        <CustomAlert variant={separateFeeMismatch ? 'warning' : 'success'}>
          <Typography
            sx={{
              fontSize: 14,
            }}
          >
            {separateFeeMismatch
              ? t('cashManagement.link.separate-fee-amount-mismatch')
              : t('cashManagement.link.separate-fee-amount-no-mismatch')}
          </Typography>
        </CustomAlert>
      ) : null}
      {(cashImmediateFeeAmountCents || linkedImmediateFeeAmountCents) &&
      immediateFeeMismatch ? (
        <CustomAlert variant={immediateFeeMismatch ? 'warning' : 'success'}>
          <Typography
            sx={{
              fontSize: 14,
            }}
          >
            {immediateFeeMismatch
              ? t('cashManagement.link.immediate-fee-amount-mismatch')
              : t('cashManagement.link.immediate-fee-amount-no-mismatch')}
          </Typography>
        </CustomAlert>
      ) : null}
    </Stack>
  );
};

interface RowProps {
  icon: ReactNode;
  label: ReactNode;
  value: ReactNode;
  color?: string;
  isLoading?: boolean;
}

const Row: FC<RowProps> = ({ icon, label, value, color, isLoading }) => {
  return (
    <Stack
      direction="row"
      sx={{
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 4,
      }}
    >
      <Stack
        direction="row"
        sx={{
          gap: 1,
          alignItems: 'center',
        }}
      >
        {icon}
        <Typography
          color={color ?? 'text.primary'}
          sx={{
            fontSize: 14,
          }}
        >
          {label}
        </Typography>
      </Stack>
      {isLoading && (
        <Typography
          color={color ?? 'text.primary'}
          sx={{
            fontSize: 14,
          }}
        >
          <Skeleton variant="text" width={100} />
        </Typography>
      )}
      {!isLoading && value}
    </Stack>
  );
};

export const DateIcon = ({ color }: { color?: string }): JSX.Element => {
  return (
    <Iconify
      sx={{ mt: '-4px' }}
      icon="eva:calendar-fill"
      color={color ?? 'grey.600'}
    />
  );
};

const CashIcon = ({ color }: { color?: string }) => {
  return (
    <Iconify
      sx={{ mt: '-2px' }}
      icon="majesticons:euro-circle"
      color={color ?? 'grey.600'}
    />
  );
};

const LinkedIcon = ({ color }: { color?: string }) => {
  return (
    <Iconify
      sx={{ mt: '-1px' }}
      icon="eva:link-2-fill"
      color={color ?? 'grey.600'}
    />
  );
};

const ReceiptIcon = ({ color }: { color?: string }) => {
  return (
    <Iconify
      sx={{ mt: '-1px' }}
      icon={'mdi:receipt-text'}
      color={color ?? 'grey.600'}
    />
  );
};

const DotIcon = ({ color }: { color?: string }) => {
  return (
    <Iconify sx={{ mt: '-1px' }} icon={'mdi:dot'} color={color ?? 'grey.600'} />
  );
};
